import React from "react"
import CalendarTutorial from "../../components/calendar-tutorial"

const tutorial = {
  title: "Create a Visual Homeschool Weekly Schedule",
  description:
    "Plan a homeschool week with subject icons, reusable activities, separate calendars, and events drafted in Magic Generate.",
  intro:
    "Make learning activities easy to see across the week. Build a small homeschool plan with clear subject names and icons, save activities you will use again, and keep different learners’ schedules in separate calendars when that helps.",
  rows: [
    ["Monday", "9:00 am", "Reading", "30 min"],
    ["Tuesday", "9:30 am", "Maths", "30 min"],
    ["Wednesday", "10:00 am", "Science exploration", "45 min"],
    ["Thursday", "9:00 am", "Writing", "30 min"],
    ["Friday", "11:00 am", "Art", "45 min"],
  ],
  prompt:
    "For the week starting Monday [YYYY-MM-DD], create these homeschool events:\nMonday 9am Reading for 30 minutes\nTuesday 9:30am Maths for 30 minutes\nWednesday 10am Science exploration for 45 minutes\nThursday 9am Writing for 30 minutes\nFriday 11am Art for 45 minutes",
  steps: [
    {
      title: "1. Choose what belongs on the calendar",
      paragraphs: [
        "Start with the activities you want to make visible, such as reading, maths, a science project, and art. Add fixed commitments first and leave space between activities for breaks and changes of pace.",
        "The example is a small planning exercise, not a complete curriculum. Replace it with the learning activities and durations you already intend to use.",
      ],
    },
    {
      title: "2. Create and name your homeschool calendar",
      paragraphs: [
        "Choose New blank calendar, then open Calendar Settings to enter a title such as “Homeschool — Weekly Plan”. Navigate to your chosen week.",
        "For different learners or groups, create separate calendars with recognisable names. Use the calendar switcher to choose the one you want to edit; check its title before adding events.",
      ],
    },
    {
      title: "3. Draft the week with Magic Generate",
      paragraphs: [
        "Replace the bracketed Monday date in the example prompt and send it in Magic Generate. Check each suggested day, time, title, and duration. Select Add to calendar once the result matches your plan.",
        "You can also build the week manually by selecting an activity and clicking a time slot. Use one approach for each event so you do not add the same lesson twice.",
      ],
    },
    {
      title: "4. Make subjects easy to recognise",
      paragraphs: [
        "Open an event and choose an icon that fits the activity: a book for reading, a calculator for maths, or an art icon for creative work. Keep the written title specific enough to understand without relying on the icon.",
        "Review the week as a whole. Adjust times and durations in the event details wherever the plan needs more breathing room.",
      ],
    },
    {
      title: "5. Keep useful activities ready to reuse",
      paragraphs: [
        "In the Activities panel, choose Add activity to save a lesson type you want to use again. Give it a clear title and a suitable icon.",
        "Select the saved activity and click a calendar time slot to place it. Review the resulting event’s time and duration. Reusable activities make future entries easier; they do not automatically create a repeating series.",
      ],
    },
    {
      title: "6. Share the plan when someone else needs it",
      paragraphs: [
        "If another caregiver or tutor needs to see the schedule, use Publish calendar to create a read-only link. Review the content and choose an optional password before publishing.",
        "After editing the plan, select Republish to update the same link. Ask viewers to reopen or refresh it to see the latest published version.",
      ],
    },
  ],
  cta: "Create your homeschool calendar",
  relatedUrl: "/use-cases/after-school-activity-schedule-ai/",
  relatedTitle: "Build an After-School Activity Schedule with AI",
}

export default function TutorialPage() {
  return <CalendarTutorial {...tutorial} />
}
